import { useQuery } from '@tanstack/react-query';
import { AlertTriangleIcon, CopyIcon, TrashIcon } from 'lucide-react';
import { type JSX, useState } from 'react';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import {
  artifactShareUrl,
  artifactSharesQueryOptions,
  isArtifactShareActive,
  useRevokeArtifactShare,
  type ArtifactShareSubject,
  type ArtifactShareView,
} from '@/lib/api/artifact-shares.js';
import { rememberedGroupName } from '@/lib/api/directory-groups.js';
import { copyToClipboard } from '@/lib/clipboard.js';
import { safeHttpUrl } from '@/lib/safe-url.js';

/**
 * What each access mode is called on screen. Keyed on a field the server can widen ahead of this build,
 * so an unknown mode reads as the neutral label rather than rendering `undefined`.
 */
const MODE_LABEL: Record<string, string> = {
  named: 'Specific people and groups',
  internal: 'Customertimes employees',
  public: 'Anyone with the link',
};

/** Date + time, so two shares made on the same day are still told apart. */
function formatDateTime(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * One recipient of a share, in words.
 *
 * A person the directory could not resolve is shown as their own id — it is all there is, and it is at
 * least a handle the author can search for. A GROUP is different: its `displayName` is always null by
 * design (identity resolves users, not groups), so the name comes from the picker that addressed the
 * share, and where nothing remembers it the row says plainly that the recipient is a group. An Entra
 * object id on screen would tell the author nothing at all about who has their file.
 */
function subjectLabel(subject: ArtifactShareSubject): string {
  if (subject.subjectType !== 'entra_group') {
    return subject.displayName ?? subject.subjectId;
  }
  const name = subject.displayName ?? rememberedGroupName(subject.subjectId);
  return name !== null ? `${name} (group)` : 'A group (name unavailable)';
}

/** Who a share is addressed to, or the mode's own label when it names nobody. */
function recipientsLine(share: ArtifactShareView): string {
  if (share.subjects.length === 0) {
    return MODE_LABEL[share.accessMode] ?? 'Unknown audience';
  }
  return share.subjects.map(subjectLabel).join(', ');
}

/**
 * What the author is told about the Teams notification that went with a `named` share, or nothing at all.
 *
 * Silence is the correct answer for a share where both fields are null, and that is the ORDINARY case: a
 * platform with no Secretary configured notifies nobody, and a share addressed to a group notifies nobody
 * by design. Neither is a failure, so neither earns a badge, a warning or a "pending".
 *
 * A failed notification, on the other hand, has to say both halves. An author who reads "not delivered"
 * and nothing else concludes the share failed, and then re-shares the file or gives up — when in fact the
 * link works and all that is missing is a message they can send themselves.
 */
function notificationNote(share: ArtifactShareView): string | null {
  if (share.notifyError === null) return null;
  const reason = share.notifyError.trim().replace(/\.$/, '');
  const problem =
    reason.length > 0
      ? `Notification not delivered: ${reason}.`
      : 'Notification not delivered.';
  return `${problem} The link still works — send it to them yourself.`;
}

/** Who has already taken their copy — the one thing revoking can no longer reach. */
function claimsLine(share: ArtifactShareView): string {
  if (share.claims.length === 0) return 'Not taken yet';
  const names = share.claims
    .map((claim) => claim.displayName ?? claim.userId)
    .join(', ');
  return `Taken by ${names}`;
}

/**
 * This file's own shares — the author's answer to "who did I send this to, and who has already got it".
 *
 * Only shares that still open are listed. A revoked or expired one is not history worth keeping on a
 * dialog: it grants nothing, and the copies that came out of it are the recipients' files now, which no
 * row here could speak for.
 *
 * A row also carries what became of the Teams notification, when there is anything to say — see
 * {@link notificationNote}.
 */
export function ArtifactShareList({
  workspaceId,
  artifactId,
}: {
  workspaceId: string;
  artifactId: string;
}): JSX.Element {
  const sharesQuery = useQuery(
    artifactSharesQueryOptions(workspaceId, artifactId),
  );
  const revoke = useRevokeArtifactShare();
  const [pendingRevokeId, setPendingRevokeId] = useState<string | null>(null);

  const active = (sharesQuery.data ?? []).filter((share) =>
    isArtifactShareActive(share),
  );

  if (active.length === 0) {
    let message = 'Not shared outside the workspace yet.';
    if (sharesQuery.isLoading) message = 'Loading…';
    else if (sharesQuery.isError) message = "Couldn't load the shares.";
    return <p className="text-xs text-muted-foreground">{message}</p>;
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-col divide-y divide-border/40">
        {active.map((share) => {
          const url = safeHttpUrl(artifactShareUrl(share.id));
          const note = notificationNote(share);
          return (
            <div key={share.id} className="flex items-start gap-2 py-2">
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className="text-[10px]">
                    {MODE_LABEL[share.accessMode] ?? 'Shared'}
                  </Badge>
                  <span className="min-w-0 truncate text-xs font-medium text-foreground">
                    {recipientsLine(share)}
                  </span>
                </div>
                <span className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                  <span>{formatDateTime(share.createdAt)}</span>
                  <span aria-hidden>·</span>
                  <span>{claimsLine(share)}</span>
                  {share.notifiedAt !== null && (
                    <>
                      <span aria-hidden>·</span>
                      <span>{`Notified ${formatDateTime(share.notifiedAt)}`}</span>
                    </>
                  )}
                </span>
                {note !== null && (
                  <span className="flex items-start gap-1.5 text-xs text-warning-600">
                    <AlertTriangleIcon
                      className="mt-0.5 size-3.5 shrink-0"
                      aria-hidden
                    />
                    <span>{note}</span>
                  </span>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-1">
                {url !== '' && (
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Copy share link"
                    onClick={() =>
                      void copyToClipboard(url, 'Share link copied')
                    }
                  >
                    <CopyIcon className="size-4" strokeWidth={1.75} />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Revoke share"
                  onClick={() => setPendingRevokeId(share.id)}
                >
                  <TrashIcon className="size-4" strokeWidth={1.75} />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <ConfirmRevokeArtifactShareDialog
        open={pendingRevokeId !== null}
        pending={revoke.isPending}
        onOpenChange={(next) => {
          if (!next) setPendingRevokeId(null);
        }}
        onConfirm={() => {
          if (pendingRevokeId !== null) {
            revoke.mutate({
              workspaceId,
              artifactId,
              shareId: pendingRevokeId,
            });
          }
          setPendingRevokeId(null);
        }}
      />
    </div>
  );
}

/**
 * Revoke confirmation, built like the chat share's own: an AppDialog, never `window.confirm`, which
 * Teams suppresses.
 *
 * Its words carry the one fact revoking cannot change. The link stops working at once, and a copy
 * somebody already took stays theirs — that is the whole shape of this feature, and the moment to say
 * it is the moment the author expects the opposite.
 */
function ConfirmRevokeArtifactShareDialog({
  open,
  onOpenChange,
  onConfirm,
  pending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  pending: boolean;
}): JSX.Element {
  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Revoke this share?"
      description="This cannot be undone."
    >
      <div className="mt-3 flex flex-col gap-4">
        <p className="text-sm text-muted-foreground">
          The link stops working immediately, so nobody else can take the file.
          A copy somebody has already taken stays theirs — revoking cannot reach
          it.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive-outline"
            size="sm"
            disabled={pending}
            onClick={onConfirm}
          >
            {pending ? 'Revoking…' : 'Revoke'}
          </Button>
        </div>
      </div>
    </AppDialog>
  );
}
